import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardList, Check, X, Mail, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { pending: 'border-amber-500/30 text-amber-400', approved: 'border-emerald-500/30 text-emerald-400', denied: 'border-red-500/30 text-red-400', invited: 'border-blue-500/30 text-blue-400' };

export default function AdminWaitlistPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [inviting, setInviting] = useState(null);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try { const params = {}; if (filter) params.status = filter; const res = await adminAPI.waitlist(params); setItems(res.data.items || []); setStats(res.data.stats || {}); } catch {}
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try { await adminAPI.updateWaitlist(id, { status }); toast.success(`Marked ${status}`); load(); } catch { toast.error('Failed'); }
  };

  const sendInvite = async (id) => {
    setInviting(id);
    try { const res = await adminAPI.inviteWaitlist(id); res.data.ok ? toast.success(res.data.message) : toast.error(res.data.error || 'Failed'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Invite failed'); }
    setInviting(null);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-waitlist">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Waitlist</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Manage join requests and invitations</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1.5 text-[10px]">
            <Badge variant="outline" className="border-amber-500/30">Pending: {stats.pending || 0}</Badge>
            <Badge variant="outline" className="border-emerald-500/30">Approved: {stats.approved || 0}</Badge>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[110px] h-7 text-[10px]"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="denied">Denied</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><ClipboardList className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No waitlist entries.</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <Card key={item.id}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="hidden sm:flex w-10 h-10 rounded-full bg-primary/10 items-center justify-center flex-shrink-0">
                    <UserPlus className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs sm:text-sm font-medium">{item.name || 'No Name'}</span>
                      <Badge variant="outline" className={`text-[9px] ${STATUS_COLORS[item.status || 'pending']}`}>{item.status || 'pending'}</Badge>
                    </div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">{item.email}</p>
                    {item.organization && <p className="text-[10px] text-muted-foreground">Org: {item.organization}</p>}
                    {item.use_case && <p className="text-[10px] text-muted-foreground truncate">Use: {item.use_case}</p>}
                    <p className="text-[9px] text-muted-foreground mt-0.5">{new Date(item.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {(!item.status || item.status === 'pending') && (
                      <>
                        <Button variant="outline" size="sm" className="h-7 text-[10px] sm:text-xs gap-1" onClick={() => updateStatus(item.id, 'approved')}><Check className="w-3 h-3" /> <span className="hidden sm:inline">Approve</span></Button>
                        <Button variant="outline" size="sm" className="h-7 text-[10px] sm:text-xs gap-1 text-red-400" onClick={() => updateStatus(item.id, 'denied')}><X className="w-3 h-3" /> <span className="hidden sm:inline">Deny</span></Button>
                      </>
                    )}
                    {item.status === 'approved' && (
                      <Button variant="outline" size="sm" className="h-7 text-[10px] sm:text-xs gap-1" onClick={() => sendInvite(item.id)} disabled={inviting === item.id}>
                        {inviting === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} <span className="hidden sm:inline">Invite</span>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
